import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, Plus, Minus, MapPin } from 'lucide-react';

// Inline sport SVGs — Lucide doesn't ship shuttlecock / wiffle-ball glyphs,
// so we use lightweight stroke icons that match Lucide's visual language.
// Color via `currentColor` so we can tint with Tailwind `text-*` classes.
function ShuttlecockIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="6" r="2.5" />
      <path d="M9.5 8 L7 18" />
      <path d="M12 8.5 L12 19" />
      <path d="M14.5 8 L17 18" />
      <path d="M6.5 18 L17.5 18" />
    </svg>
  );
}

function WiffleBallIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="8" cy="9" r="0.9" fill="currentColor" />
      <circle cx="15" cy="8" r="0.9" fill="currentColor" />
      <circle cx="16" cy="14" r="0.9" fill="currentColor" />
      <circle cx="9" cy="15" r="0.9" fill="currentColor" />
      <circle cx="12" cy="12" r="0.9" fill="currentColor" />
    </svg>
  );
}

function SportIcon({ sport, className = 'w-5 h-5' }) {
  if (sport === 'badminton') return <ShuttlecockIcon className={className} />;
  if (sport === 'pickleball') return <WiffleBallIcon className={className} />;
  return null;
}

function SportLabel({ sport }) {
  return <span className="capitalize">{sport}</span>;
}

function SportPill({ sport, selected, onSelect, price }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      data-testid={`sport-pill-${sport}${selected ? '-selected' : ''}`}
      className={[
        'flex-1 min-w-0 rounded-2xl border-2 px-4 py-3 transition-all',
        'flex flex-col items-center gap-1 text-center',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D2FF00]/70',
        selected
          ? 'border-[#D2FF00] bg-[#D2FF00]/20 shadow-sm scale-[1.01]'
          : 'border-border bg-background hover:border-[#D2FF00]/60 hover:bg-[#D2FF00]/10',
      ].join(' ')}
    >
      <div className="flex items-center gap-2">
        <span
          className={[
            'inline-flex items-center justify-center w-5 h-5 rounded-full border-2 transition-colors',
            selected ? 'border-[#D2FF00] bg-[#D2FF00]' : 'border-muted-foreground/40',
          ].join(' ')}
          aria-hidden="true"
        >
          {selected && <span className="w-2 h-2 rounded-full bg-background" />}
        </span>
        <SportIcon sport={sport} className={selected ? 'w-5 h-5 text-foreground' : 'w-5 h-5 text-muted-foreground'} />
        <span className={selected ? 'font-bold text-foreground' : 'font-medium text-muted-foreground'}>
          <SportLabel sport={sport} />
        </span>
      </div>
      {price != null && (
        <span className={selected ? 'text-xs font-semibold text-foreground' : 'text-xs text-muted-foreground'}>
          ${price}/hr
        </span>
      )}
    </button>
  );
}

export default function StepCourts({
  selectedLocation, selectedDate, selectedTimeSlot, duration,
  courtsRequested, courtsPricing, sportPriceMap = {}, availableSports,
  specialRequests,
  onAddCourt, onRemoveCourt, onUpdateSport, onSpecialRequestsChange,
  onBack, onNext,
}) {
  return (
    <div className="space-y-6 animate-slide-up" data-testid="step-courts">
      <div>
        <h2 className="text-xl font-bold font-heading uppercase mb-4">Select Courts & Sports</h2>

        <Card className="p-3 mb-4 bg-muted/50">
          <p className="text-sm">
            <MapPin className="w-4 h-4 inline mr-1" />
            <strong>{selectedLocation?.name}</strong> - {selectedDate} at {selectedTimeSlot}
          </p>
        </Card>

        {courtsRequested.map((court, index) => (
          <Card key={court._uid || `court-${index}`} className="p-4 mb-3 glass-card">
            <div className="flex items-center justify-between mb-3">
              <Label className="uppercase text-xs font-bold flex items-center gap-2">
                <SportIcon sport={court.sport} className="w-4 h-4 text-[#7BCB00]" />
                Court {index + 1}
                <span className="text-muted-foreground font-medium normal-case">
                  — <SportLabel sport={court.sport} />
                </span>
              </Label>
              {courtsRequested.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveCourt(index)}
                  data-testid={`remove-court-${index}`}
                >
                  <Minus className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div
              role="radiogroup"
              aria-label={`Sport for court ${index + 1}`}
              className="flex gap-2"
              data-testid={`sport-pill-group-${index}`}
            >
              {availableSports.map((sport) => (
                <SportPill
                  key={sport}
                  sport={sport}
                  selected={court.sport === sport}
                  onSelect={() => onUpdateSport(index, sport)}
                  price={
                    court.sport === sport
                      ? courtsPricing[index]?.price_per_hour ?? sportPriceMap[sport]
                      : sportPriceMap[sport]
                  }
                />
              ))}
            </div>

            {courtsPricing[index] && (
              <p className="text-sm text-muted-foreground mt-3">
                ${courtsPricing[index].price_per_hour}/hr × {duration}hr = ${courtsPricing[index].total}
              </p>
            )}
          </Card>
        ))}

        {courtsRequested.length < 5 && (
          <Button
            variant="outline"
            onClick={onAddCourt}
            data-testid="add-court-button"
            className="w-full rounded-full border-2 border-dashed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Court
          </Button>
        )}
      </div>

      <div>
        <Label htmlFor="special-requests" className="uppercase text-xs font-bold text-muted-foreground mb-1.5 block">
          Special Requests (Optional)
        </Label>
        <Textarea
          id="special-requests"
          data-testid="special-requests-input"
          placeholder="Any special requirements? E.g., preferred court number, accessibility needs..."
          value={specialRequests}
          onChange={(e) => onSpecialRequestsChange(e.target.value)}
          className="rounded-2xl"
          rows={3}
        />
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onBack}
          data-testid="courts-back-button"
          className="flex-1 h-14 rounded-full"
        >
          Back
        </Button>
        <Button
          onClick={onNext}
          data-testid="courts-next-button"
          className="flex-1 btn-volt h-14"
        >
          Review Booking
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
